/**
 * Broken Notes Diagnostic - Admin Tool
 * 
 * Scans for and identifies broken, contaminated, or lost notes:
 * - Lost Content: Notes where text backup exists but content is empty (DATA LOSS!)
 * - Empty Notes: Notes with no meaningful content
 * - Client Mismatch: Notes where stored client_name doesn't match actual client
 * - Orphaned Drafts: Server drafts that never made it to the note
 * - Suspicious Timestamps: Notes with unusual created/updated patterns
 * - Duplicate Content: Same content in multiple notes (contamination)
 */

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { formatUtcTimestamp } from '../../utils/utcTime';
import {
  AlertTriangle,
  Search,
  Loader2,
  FileWarning,
  Users,
  Clock,
  Copy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Info,
  CheckCircle,
  FileX,
  Files,
  Download,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const severityColors = {
  critical: 'bg-red-100 border-red-500 text-red-800',
  warning: 'bg-amber-100 border-amber-500 text-amber-800',
  info: 'bg-blue-100 border-blue-500 text-blue-800',
};

const severityIcons = {
  critical: <AlertCircle className="h-4 w-4 text-red-600" />,
  warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  info: <Info className="h-4 w-4 text-blue-600" />,
};

const BrokenNotesDiagnostic = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [daysBack, setDaysBack] = useState(30);
  const [expandedNote, setExpandedNote] = useState(null);
  const [noteDetails, setNoteDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [recovering, setRecovering] = useState(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setResults(null);
    try {
      const response = await axios.get(`${API}/api/admin/diagnostics/broken-notes`, {
        params: { days_back: daysBack, limit: 100 },
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      setResults(response.data);
      
      const { summary } = response.data;
      if (summary.critical_issues > 0) {
        toast.error(`Found ${summary.critical_issues} CRITICAL issues requiring attention!`);
      } else if (summary.warnings > 0) {
        toast.warning(`Found ${summary.warnings} warnings to review`);
      } else {
        toast.success('No critical issues found');
      }
    } catch (err) {
      console.error('Diagnostic error:', err);
      toast.error(err.response?.data?.detail || 'Failed to run diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const fetchNoteDetails = async (noteId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/api/admin/diagnostics/note-details/${noteId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      setNoteDetails(response.data);
      setExpandedNote(noteId);
    } catch (err) {
      toast.error('Failed to fetch note details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const recoverFromBackup = async (noteId, backupId = null) => {
    setRecovering(noteId);
    try {
      const response = await axios.post(
        `${API}/api/admin/diagnostics/recover-from-backup/${noteId}`,
        null,
        {
          params: backupId ? { backup_id: backupId } : {},
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        }
      );
      toast.success(response.data.message || 'Note recovered successfully');
      // Refresh the diagnostic
      runDiagnostic();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recover note');
    } finally {
      setRecovering(null);
    }
  };

  const copyToClipboard = (text, label = 'Copied to clipboard') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  // Display doc_number (e.g., "DOC-URLAEU") if available, otherwise show truncated UUID
  const DocIdBadge = ({ docNumber, noteId }) => {
    const displayId = docNumber || (noteId ? `${noteId.slice(0, 8)}...` : 'N/A');
    const copyId = docNumber || noteId || '';
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(copyId, 'Doc ID copied');
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 hover:bg-gray-300 rounded text-xs font-mono text-gray-700 transition-colors"
        title={`Click to copy: ${copyId}`}
      >
        <Copy className="h-3 w-3" />
        {displayId}
      </button>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return formatUtcTimestamp(dateStr) || 'Unknown';
    } catch {
      return dateStr;
    }
  };

  const CollapsibleIssueCard = ({ title, icon, issues, severity, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    if (!issues || issues.length === 0) return null;
    
    return (
      <div className={`border-2 rounded-lg mb-4 ${severityColors[severity]}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-opacity-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="font-semibold">{title}</h3>
            <Badge variant={severity === 'critical' ? 'destructive' : 'secondary'}>
              {issues.length}
            </Badge>
          </div>
          {isOpen ? (
            <ChevronUp className="h-5 w-5 flex-shrink-0" />
          ) : (
            <ChevronDown className="h-5 w-5 flex-shrink-0" />
          )}
        </button>
        {isOpen && (
          <div className="px-4 pb-4 border-t border-current border-opacity-20">
            <div className="pt-3">
              {children}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileWarning className="h-5 w-5 text-red-500" />
          Broken Notes Diagnostic
        </CardTitle>
        <p className="text-sm text-gray-500">
          Scan for lost content, contamination, and data integrity issues
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Scan last</span>
            <Input
              type="number"
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value) || 30)}
              className="w-20"
              min={1}
              max={365}
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <Button
            onClick={runDiagnostic}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Run Diagnostic
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gray-100 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold">{results.summary.total_notes_scanned}</div>
                <div className="text-xs text-gray-600">Notes Scanned</div>
              </div>
              <div className={`rounded-lg p-3 text-center ${results.summary.critical_issues > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                <div className={`text-2xl font-bold ${results.summary.critical_issues > 0 ? 'text-red-600' : ''}`}>
                  {results.summary.critical_issues}
                </div>
                <div className="text-xs text-gray-600">Critical Issues</div>
              </div>
              <div className={`rounded-lg p-3 text-center ${results.summary.warnings > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <div className={`text-2xl font-bold ${results.summary.warnings > 0 ? 'text-amber-600' : ''}`}>
                  {results.summary.warnings}
                </div>
                <div className="text-xs text-gray-600">Warnings</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{results.summary.info}</div>
                <div className="text-xs text-gray-600">Info</div>
              </div>
            </div>

            {/* No Issues */}
            {results.summary.critical_issues === 0 && results.summary.warnings === 0 && results.summary.info === 0 && (
              <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-800 font-medium">No issues found! All notes appear healthy.</span>
              </div>
            )}

            {/* CRITICAL: Lost Content */}
            <CollapsibleIssueCard
              title="LOST CONTENT - Data Recovery Needed!"
              icon={<AlertCircle className="h-5 w-5 text-red-600" />}
              issues={results.issues.lost_content}
              severity="critical"
              defaultOpen={true}
            >
              <p className="text-sm mb-3">
                These notes have text backups but empty content. The provider's work was lost but may be recoverable.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.issues.lost_content.map((item, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-red-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.client_name || 'Unknown Client'}</span>
                          <DocIdBadge docNumber={item.doc_number} noteId={item.note_id} />
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.template_name || '(no template)'} • {formatDate(item.created_at)}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Backup has {item.backup_text_length} characters
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchNoteDetails(item.note_id)}
                        >
                          Details
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => recoverFromBackup(item.note_id)}
                          disabled={recovering === item.note_id}
                        >
                          {recovering === item.note_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Recover
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {item.backup_text_preview && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs max-h-20 overflow-y-auto">
                        <strong>Preview:</strong> {item.backup_text_preview}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleIssueCard>

            {/* CRITICAL: Duplicate Content Across Different Clients */}
            {results.issues.duplicate_content && results.issues.duplicate_content.filter(d => d.different_clients).length > 0 && (
              <CollapsibleIssueCard
                title="CRITICAL: Same Content on Different Clients!"
                icon={<AlertCircle className="h-5 w-5 text-red-600" />}
                issues={results.issues.duplicate_content.filter(d => d.different_clients)}
                severity="critical"
                defaultOpen={true}
              >
                <p className="text-sm mb-3">
                  <strong>These notes have identical content but belong to DIFFERENT clients.</strong> This may indicate content contamination - one client's note was saved to another client's chart.
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {results.issues.duplicate_content.filter(d => d.different_clients).map((item, idx) => (
                    <div key={idx} className="bg-white rounded p-3 border-2 border-red-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="destructive">
                          {item.note_count} notes with identical content
                        </Badge>
                        <span className="text-red-600 text-sm font-bold">DIFFERENT CLIENTS!</span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <strong>Affected clients:</strong>
                      </div>
                      <ul className="mt-1 text-sm text-gray-600 list-disc list-inside">
                        {item.notes.map((n, nIdx) => (
                          <li key={nIdx} className="flex items-center gap-2 ml-4 list-none">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                            <span>{n.client_name} - {n.template_name || '(no template)'}</span>
                            <DocIdBadge docNumber={n.doc_number} noteId={n.note_id} />
                            <span className="text-gray-400">({formatDate(n.created_at)})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CollapsibleIssueCard>
            )}

            {/* CRITICAL: Client ID vs Name Mismatch - Real Contamination */}
            <CollapsibleIssueCard
              title="CRITICAL: Client ID vs Name Mismatch"
              icon={<AlertCircle className="h-5 w-5 text-red-600" />}
              issues={results.issues.client_mismatch}
              severity="critical"
              defaultOpen={true}
            >
              <p className="text-sm mb-3">
                <strong>These notes have a client_id pointing to one person, but the stored client_name is for a DIFFERENT person.</strong> This is real contamination - the note was saved to the wrong client's chart.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.issues.client_mismatch.map((item, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border-2 border-red-300">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <DocIdBadge docNumber={item.doc_number} noteId={item.note_id} />
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-red-700">Note says:</span> "{item.stored_client_name}"
                        </div>
                        <div className="text-sm mt-1">
                          <span className="font-medium text-green-700">Client ID points to:</span> "{item.actual_client_name}"
                        </div>
                        <div className="text-xs text-gray-500 mt-2">{item.template_name} • {formatDate(item.created_at)}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => fetchNoteDetails(item.note_id)}>
                        Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleIssueCard>

            {/* WARNING: Duplicate Content (Same Client - less serious) */}
            {results.issues.duplicate_content && results.issues.duplicate_content.filter(d => !d.different_clients).length > 0 && (
              <CollapsibleIssueCard
                title="Duplicate Content (Same Client)"
                icon={<Files className="h-5 w-5 text-amber-600" />}
                issues={results.issues.duplicate_content.filter(d => !d.different_clients)}
                severity="warning"
              >
                <p className="text-sm mb-3">
                  These notes have identical content for the same client. May be accidental duplicates.
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {results.issues.duplicate_content.filter(d => !d.different_clients).map((item, idx) => (
                    <div key={idx} className="bg-white rounded p-2 border border-amber-200 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {item.note_count} notes
                        </Badge>
                      </div>
                      <div className="mt-1 space-y-1">
                        {item.notes.map((n, nIdx) => (
                          <div key={nIdx} className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{n.client_name}</span>
                            <DocIdBadge docNumber={n.doc_number} noteId={n.note_id} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleIssueCard>
            )}

            {/* WARNING: Empty Notes */}
            <CollapsibleIssueCard
              title="Empty Notes"
              icon={<FileX className="h-5 w-5 text-amber-600" />}
              issues={results.issues.empty_notes}
              severity="warning"
            >
              <p className="text-sm mb-3">
                These notes have no meaningful content. May be drafts or failed saves.
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto text-sm">
                {results.issues.empty_notes.slice(0, 20).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white rounded p-2 border border-amber-100">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.client_name || 'Unknown'}</span>
                      <DocIdBadge docNumber={item.doc_number} noteId={item.note_id} />
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500">{item.template_name || '(no template)'}</span>
                    </div>
                    <div className="text-xs text-gray-400">{formatDate(item.created_at)}</div>
                  </div>
                ))}
                {results.issues.empty_notes.length > 20 && (
                  <div className="text-center text-gray-400 text-xs">
                    ...and {results.issues.empty_notes.length - 20} more
                  </div>
                )}
              </div>
            </CollapsibleIssueCard>

            {/* INFO: Suspicious Timestamps */}
            <CollapsibleIssueCard
              title="Suspicious Timestamps"
              icon={<Clock className="h-5 w-5 text-blue-600" />}
              issues={results.issues.suspicious_timestamps}
              severity="info"
            >
              <p className="text-sm mb-3">
                Notes updated immediately after creation with no content. May indicate autosave overwrote data.
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto text-sm">
                {results.issues.suspicious_timestamps.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="bg-white rounded p-2 border border-blue-100 text-xs flex items-center gap-2">
                    <span className="font-medium">{item.client_name}</span>
                    <DocIdBadge docNumber={item.doc_number} noteId={item.note_id} />
                    <span className="text-gray-400">•</span>
                    <span>Updated {item.seconds_between.toFixed(1)}s after creation</span>
                  </div>
                ))}
              </div>
            </CollapsibleIssueCard>

            {/* INFO: Orphaned Drafts */}
            <CollapsibleIssueCard
              title="Orphaned Drafts"
              icon={<FileWarning className="h-5 w-5 text-blue-600" />}
              issues={results.issues.orphaned_drafts}
              severity="info"
            >
              <p className="text-sm mb-3">
                Server-side drafts with more content than the actual note. Save may have failed.
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto text-sm">
                {results.issues.orphaned_drafts.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="bg-white rounded p-2 border border-blue-100 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <DocIdBadge docNumber={item.doc_number} noteId={item.note_id} />
                      <span className="text-xs">
                        Draft has {item.draft_fields} fields, note has {item.note_fields}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(item.draft_updated_at)}</span>
                  </div>
                ))}
              </div>
            </CollapsibleIssueCard>
          </div>
        )}

        {/* Note Details Dialog */}
        <Dialog open={!!expandedNote} onOpenChange={(open) => !open && setExpandedNote(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Note Diagnostic Details</DialogTitle>
            </DialogHeader>
            
            {loadingDetails ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : noteDetails ? (
              <div className="space-y-4">
                {/* Note Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Note Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Note ID:</span> {noteDetails.note.id}</div>
                    <div><span className="text-gray-500">Status:</span> {noteDetails.note.status}</div>
                    <div><span className="text-gray-500">Client (stored):</span> {noteDetails.note.client_name_stored || 'None'}</div>
                    <div><span className="text-gray-500">Client (actual):</span> {noteDetails.note.client_name_actual || 'None'}</div>
                    <div><span className="text-gray-500">Template:</span> {noteDetails.note.template_name}</div>
                    <div><span className="text-gray-500">Filled Fields:</span> {noteDetails.note.filled_fields}</div>
                    <div><span className="text-gray-500">Created:</span> {formatDate(noteDetails.note.created_at)}</div>
                    <div><span className="text-gray-500">Updated:</span> {formatDate(noteDetails.note.updated_at)}</div>
                  </div>
                  
                  {noteDetails.note.content_preview && (
                    <div className="mt-3">
                      <div className="text-gray-500 text-sm mb-1">Content Preview:</div>
                      <div className="bg-white p-2 rounded border text-xs max-h-24 overflow-y-auto">
                        {noteDetails.note.content_preview}
                      </div>
                    </div>
                  )}
                </div>

                {/* Diagnosis */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Diagnosis</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      {noteDetails.diagnosis.has_content ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span>Has Content: {noteDetails.diagnosis.has_content ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {noteDetails.diagnosis.has_backups ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Info className="h-4 w-4 text-gray-400" />
                      )}
                      <span>Has Text Backups: {noteDetails.diagnosis.has_backups ? 'Yes' : 'No'}</span>
                    </div>
                    {noteDetails.diagnosis.potential_data_loss && (
                      <div className="flex items-center gap-2 text-red-600 font-medium">
                        <AlertCircle className="h-4 w-4" />
                        <span>POTENTIAL DATA LOSS - Backup exists but note is empty!</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Backups */}
                {noteDetails.text_backups.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Text Backups ({noteDetails.text_backups.length})</h4>
                    <div className="space-y-3">
                      {noteDetails.text_backups.map((backup, idx) => (
                        <div key={idx} className="bg-gray-50 rounded p-3 border">
                          <div className="flex justify-between items-start mb-2">
                            <div className="text-xs text-gray-500">
                              {formatDate(backup.created_at)} • {backup.text_length} chars
                              {backup.has_structured_data && (
                                <span className="ml-2 text-green-600">(structured data found)</span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(backup.readable_text || backup.full_text)}
                                title="Copy content"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              {noteDetails.diagnosis.potential_data_loss && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => recoverFromBackup(expandedNote, backup.id)}
                                  disabled={recovering === expandedNote}
                                >
                                  Recover
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Tabbed view for readable vs raw content */}
                          <Tabs defaultValue="readable" className="w-full">
                            <TabsList className="h-7 mb-2">
                              <TabsTrigger value="readable" className="text-xs px-2 py-1 h-6">
                                Readable Content
                              </TabsTrigger>
                              <TabsTrigger value="raw" className="text-xs px-2 py-1 h-6">
                                Raw JSON
                              </TabsTrigger>
                            </TabsList>
                            <TabsContent value="readable" className="mt-0">
                              <div className="text-xs bg-white p-2 rounded border max-h-64 overflow-y-auto whitespace-pre-wrap">
                                {backup.readable_text || "(No readable content extracted)"}
                              </div>
                            </TabsContent>
                            <TabsContent value="raw" className="mt-0">
                              <div className="text-xs bg-white p-2 rounded border max-h-64 overflow-y-auto whitespace-pre-wrap font-mono">
                                {backup.full_text || "(empty)"}
                              </div>
                            </TabsContent>
                          </Tabs>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Server Drafts */}
                {noteDetails.server_drafts.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Server Drafts ({noteDetails.server_drafts.length})</h4>
                    <div className="space-y-1">
                      {noteDetails.server_drafts.map((draft, idx) => (
                        <div key={idx} className="bg-gray-50 rounded p-2 text-sm flex justify-between">
                          <span>{draft.filled_fields} fields</span>
                          <span className="text-gray-500">{formatDate(draft.updated_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 p-4">No details available</div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default BrokenNotesDiagnostic;
